async function ca_updateProcessingDelayTotalField(field, unitPrice) {
  if (!field) return;

  const verificationQuantity = await ca_getCardVerificationQuantity();

  if (!verificationQuantity || verificationQuantity === 0) return;

  const total = verificationQuantity * unitPrice;
  field.textContent = CollectAura.formatMoney(total);
}

/**
 * Removes all existing processing delay items from the cart and adds a new one with the specified variant ID.
 *
 * @async
 * @param {number|string} variantId - The ID of the processing delay variant to add to the cart.
 * @returns {Promise<void>} Resolves when the processing delay item has been set and the cart summary updated.
 */
async function ca_setProcessingDelay(variantId) {
  await ca_removeAllProcessingDelays();

  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: parseInt(variantId),
          quantity: 1,
        },
      ],
    }),
  });

  await ca_updateCartSummary();
}

/**
 * Recomputes processing delays by removing all existing processing delays and adding a new variant to the cart.
 *
 * @async
 * @param {number|string|null} newVariantId - The ID of the new variant to add to the cart. If null or undefined, uses the deleted variant ID.
 * @param {number|null} verificationQuantity - The quantity to add for the variant. If null or undefined, uses the result of `ca_getCardVerificationQuantity()`.
 * @returns {Promise<void>} Resolves when the processing delays have been recomputed and the cart summary updated.
 */
async function ca_computeProcessingDelays(newVariantId, verificationQuantity) {
  const deletedVariant = await ca_removeAllProcessingDelays();

  const variantToAdd = newVariantId ?? deletedVariant;
  const quantityToAdd = verificationQuantity ?? (await ca_getCardVerificationQuantity());

  if (!variantToAdd || !quantityToAdd) {
    return;
  }

  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: parseInt(variantToAdd),
          quantity: quantityToAdd,
        },
      ],
    }),
  });

  ca_updateCartSummary();
}

/**
 * Removes all items from the Shopify cart that have a product_type of `processing-delay`.
 *
 * Fetches the current cart, identifies all items with the specified product type,
 * and removes each one by setting its quantity to zero via the Shopify AJAX API.
 *
 * @async
 * @function
 * @returns {Promise<number|null>} The variant_id of the first removed `processing-delay` item,
 * or null if no such items were found or an error occurred.
 */
async function ca_removeAllProcessingDelays() {
  try {
    const processingItems = await ca_getProcessingDelayItems();

    if (processingItems.length === 0) {
      return null;
    }

    for (const item of processingItems) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
        }),
      });
    }

    return processingItems[0].variant_id;
  } catch (error) {
    console.error('Error while deleting processing delay:', error);
    return null;
  }
}

/**
 * Fetches the cart and returns processing-delay items.
 *
 * @async
 * @function
 * @returns {Promise<Array>} Object containing the full `cart` and the filtered `processingItems`.
 */
async function ca_getProcessingDelayItems() {
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    const processingItems = cart.items.filter((item) => item.product_type === 'processing-delay');
    return processingItems;
  } catch (error) {
    console.error('Error while fetching processing-delay items:', error);
    return [];
  }
}

/**
 * Asynchronously retrieves the quantity of items in the cart with the product type 'card-verification'.
 *
 * Fetches the current cart data from the '/cart.js' endpoint and filters the items to count how many have
 * a product_type of 'card-verification'.
 *
 * @async
 * @function
 * @returns {Promise<number|null>} The number of 'card-verification' items in the cart, or null if an error occurs.
 */
async function ca_getCardVerificationQuantity() {
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    return cart.items.reduce((accumulator, currentValue) => {
      if (currentValue.product_type !== 'card-verification') {
        return accumulator;
      }
      accumulator += currentValue.quantity;
      return accumulator;
    }, 0);
  } catch (error) {
    console.error('Error while fetching cart:', error);
    return null;
  }
}
